import { useState } from 'react';
import { Box } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { PlayArrow, Done } from '@mui/icons-material';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { authTokenState, cognitoIdState } from 'features/auth/state/authAtoms';
import { selectedOptCFDState } from 'features/optimization/state/trialAtoms';
import { displayStudyState } from 'features/optimization/state/studyAtoms';
import { fetchDLSurfFromS3 } from 'features/dl-surf/api/fetchDLSurfFromS3';
import { submitLimeStart } from 'features/lime/api/submitLimeStart';
import { fetchLimeResult } from 'features/lime/api/fetchLimeResult';
import { LIMEState } from 'features/lime/state/limeAtoms';
import { determinePresetId, OBJECTIVE_PRESETS } from 'shared/domain/objectivePresets';
import { CustomButton } from 'shared/ui/CustomButton';

export const TrialApiButtons: React.FC = () => {
  const authToken = useRecoilValue(authTokenState);
  const cognitoId = useRecoilValue(cognitoIdState);
  const selectedOptCFD = useRecoilValue(selectedOptCFDState);
  const displayStudy = useRecoilValue(displayStudyState);
  const setLimeResults = useSetRecoilState(LIMEState);

  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [startLoading, setStartLoading] = useState<boolean>(false);
  const [resultLoading, setResultLoading] = useState<boolean>(false);

  const presetId = selectedOptCFD?.presetId ?? determinePresetId({});
  const preset = OBJECTIVE_PRESETS[presetId];
  const baseKey = preset.objectives[0]?.key;
  const primaryCfdId = baseKey ? selectedOptCFD?.cfd_id?.[baseKey] : undefined;
  const studyName = displayStudy?.name ?? selectedOptCFD?.name ?? '';

  const handleDownloadClick = async () => {
    if (!primaryCfdId || !studyName) return;
    setDownloadLoading(true);
    await fetchDLSurfFromS3(authToken, studyName, primaryCfdId);
    setDownloadLoading(false);
  };

  const handleLimeStartClick = async () => {
    if (authToken === '' || !studyName || !primaryCfdId) return;
    setStartLoading(true);
    await submitLimeStart(authToken, cognitoId, studyName, primaryCfdId);
    setStartLoading(false);
  };

  const handleLimeResultClick = async () => {
    if (authToken === '' || !primaryCfdId) return;
    setResultLoading(true);
    const lime = await fetchLimeResult(authToken, primaryCfdId);
    setLimeResults(lime);
    setResultLoading(false);
  };

  return (
    <Box component="div" sx={{ display: 'flex', gap: 0.5 }}>
      <Box component="div" sx={{ width: '100%' }}>
        <CustomButton
          size='small'
          disabled={downloadLoading || !primaryCfdId || !studyName}
          onClick={handleDownloadClick}
          loading={downloadLoading}
          loadingPosition='start'
          startIcon={<DownloadIcon />}
          variant='contained'
          sx={{ fontSize: '10px', width: '100%' }}
        >
          Download
        </CustomButton>
      </Box>
      <Box component="div" sx={{ width: '100%' }}>
        <CustomButton
          size='small'
          disabled={startLoading || !primaryCfdId || !studyName}
          onClick={handleLimeStartClick}
          loading={startLoading}
          loadingPosition='start'
          startIcon={<PlayArrow />}
          variant='contained'
          sx={{ fontSize: '10px', width: '100%' }}
        >
          LIME Start
        </CustomButton>
      </Box>
      <Box component="div" sx={{ width: '100%' }}>
        <CustomButton
          size='small'
          disabled={resultLoading || !primaryCfdId}
          onClick={handleLimeResultClick}
          loading={resultLoading}
          loadingPosition='start'
          startIcon={<Done />}
          variant='contained'
          sx={{ fontSize: '10px', width: '100%' }}
        >
          Get LIME Result
        </CustomButton>
      </Box>
    </Box>
  );
};
